import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useDashboardSearchCondition } from '@/providers/DashboardSearchConditionProvider';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
  ChartData,
  ChartOptions,
  Filler,
  ChartType,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { MotionDiv } from '@/components/element/MotionDiv';
import {
  PlDashboardMonthlyAnalysis,
  usePlDashboardMonthlyAnalysis,
} from '@/hooks/api/2450_pl_dashboard_monthly_analysis';

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
  Filler
);

const createChartOptions = (suggestedMin: number, suggestedMax: number) => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'center',
        reverse: true,
        labels: {
          usePointStyle: true,
          boxWidth: 5,
          boxHeight: 5,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    scales: {
      y: {
        suggestedMin: suggestedMin,
        suggestedMax: suggestedMax,
        stacked: true,
        border: {
          display: false,
          dash: [5, 5],
        },
        grid: {
          display: true,
          drawOnChartArea: true,
          drawTicks: false,
          // borderDash: [5, 5],
          color: function (context) {
            if (context.tick.value == 0) {
              return '#000000';
            } else {
              return '#eeeeee';
            }
          },
        },
        ticks: {
          display: true,
          padding: 10,
          color: '#b2b9bf',
          font: {
            size: 11,
            family: 'Open Sans',
            style: 'normal',
            lineHeight: 2,
          },
        },
      },
      x: {
        stacked: true,
        border: {
          display: false,
          dash: [5, 5],
        },
        grid: {
          display: true,
          drawOnChartArea: true,
          drawTicks: true,
        },
        ticks: {
          display: true,
          color: '#b2b9bf',
          padding: 10,
          font: {
            size: 11,
            family: 'Open Sans',
            style: 'normal',
            lineHeight: 2,
          },
        },
      },
    },
  } as ChartOptions;
};

// 複数の配列の各要素ごとを足し合わせる
const addArrays = (...arrays: number[][]): number[] => {
  // 配列の各インデックスに対応する要素を合計
  return arrays[0].map((_, i) => arrays.reduce((sum, arr) => sum + arr[i], 0));
};

const createGradientStroke = (ctx: CanvasRenderingContext2D) => {
  const gradient = ctx.createLinearGradient(0, 230, 0, 50);
  gradient.addColorStop(1, 'rgba(130,214,22,0.2)');
  gradient.addColorStop(0.2, 'rgba(72,72,176,0.0)');
  gradient.addColorStop(0, 'rgba(130,214,22,0)');
  return gradient;
};

export const MonthlyAnalysis = () => {
  const { _w_ } = useCurrentWorkspace();
  const { plDashboardSearchCondition: searchCondition, setPlDashboardSearchCondition: setSearchCondition } =
    useDashboardSearchCondition();
  const { data, isPending } = usePlDashboardMonthlyAnalysis({ ...searchCondition, identifier: _w_!.identifier });

  return (
    <>
      <MotionDiv className="row">
        <div className="col-12">
          <div className="card mb-4">
            <div className="card-header pb-0 p-3">
              <h6 className="mb-0">月次分析サマリー（積上）</h6>
            </div>
            <div className="card-body p-3">
              {data && !isPending && (
                <CumulativeMonthlyAnalysisChart d={data} use_external_expense={searchCondition.use_external_expense} />
              )}
              {/*<canvas id='mixed-chart' className='chart-canvas' height='300px'></canvas>*/}
            </div>
          </div>
        </div>
      </MotionDiv>

      <MotionDiv className="row">
        <div className="col-12">
          <div className="card mb-4">
            <div className="card-header pb-0 p-3">
              <h6 className="mb-0">月次分析サマリー（月別）</h6>
            </div>
            <div className="card-body p-3">
              {data && !isPending && (
                <MonthlyAnalysisChart d={data} use_external_expense={searchCondition.use_external_expense} />
              )}
              {/*<canvas id='mixed-chart' className='chart-canvas' height='300px'></canvas>*/}
            </div>
          </div>
        </div>
      </MotionDiv>
    </>
  );
};

const CumulativeMonthlyAnalysisChart = ({
  d,
  use_external_expense,
}: {
  d: PlDashboardMonthlyAnalysis;
  use_external_expense: boolean;
}) => {
  const [chartData, setChartData] = useState<ChartData>({ datasets: [] });
  // const [chartOptions, setChartOptions] = useState<ChartOptions>(JSON.parse(JSON.stringify(defaultChartOptions)) as ChartOptions);
  const [chartOptions, setChartOptions] = useState<ChartOptions>({});
  const chartRef = useRef<ChartJS>(null);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const labels = d.target_year_month.map((ym) => {
      const month = moment(`${ym}01`).month();
      // return moment.monthsShort(month);
      return `${month + 1}月`;
    });

    const sumProfit =
      d.sales_completed_cumulative_amounts[11] +
      d.sales_contracting_cumulative_amounts[11] +
      d.sales_under_negotiation_cumulative_amounts[11] +
      d.sales_detail_proposal_cumulative_amounts[11] +
      d.sales_initial_proposal_cumulative_amounts[11] +
      d.sales_attack_list_cumulative_amounts[11];

    let sumLoss =
      d.purchase_completed_cumulative_amounts[11] +
      d.purchase_contracting_cumulative_amounts[11] +
      d.purchase_preparing_cumulative_amounts[11];

    if (use_external_expense) {
      sumLoss += d.external_expense_cumulative_amounts[11];
    }

    const suggestedMinMax = Math.abs(sumProfit) > Math.abs(sumLoss) ? Math.abs(sumProfit) : Math.abs(sumLoss);

    const chartDatasets = [];
    chartDatasets.push({
      type: 'line',
      label: '収入−支出',
      tension: 0.1,
      pointRadius: 0,
      pointBackgroundColor: '#82D616',
      borderColor: '#82D616',
      borderWidth: 3,
      backgroundColor: createGradientStroke(chart.ctx),
      data: d.balance_cumulative_amounts,
      fill: true,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.completed, // 契約終了
      backgroundColor: 'rgba(69, 104, 205, 1)',
      data: d.sales_completed_cumulative_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.contracting, // 契約中
      backgroundColor: 'rgba(69, 104, 205, 0.8)',
      data: d.sales_contracting_cumulative_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.under_negotiation, // 交渉中
      backgroundColor: 'rgba(116, 182, 248, 1)',
      data: d.sales_under_negotiation_cumulative_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.detail_proposal, // 詳細提案
      backgroundColor: 'rgba(116, 182, 248, 0.8)',
      data: d.sales_detail_proposal_cumulative_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.initial_proposal, // 初期提案
      backgroundColor: 'rgba(116, 182, 248, 0.6)',
      data: d.sales_initial_proposal_cumulative_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.attack_list, // アタックリスト
      backgroundColor: 'rgba(116, 182, 248, 0.4)',
      data: d.sales_attack_list_cumulative_amounts,
      maxBarThickness: 30,
    });
    if (use_external_expense) {
      chartDatasets.push({
        type: 'bar',
        label: 'Node管理外費用',
        backgroundColor: '#495057',
        data: d.external_expense_cumulative_amounts,
        maxBarThickness: 30,
      });
    }
    chartDatasets.push({
      type: 'bar',
      label: d.purchase_order_phase_labels.completed, // 契約終了
      backgroundColor: '#9542C1',
      data: d.purchase_completed_cumulative_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.purchase_order_phase_labels.contracting, // 契約中
      backgroundColor: '#A368CE',
      data: d.purchase_contracting_cumulative_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.purchase_order_phase_labels.preparing, // 稟議準備
      backgroundColor: '#E1AEF8',
      data: d.purchase_preparing_cumulative_amounts,
      maxBarThickness: 30,
    });

    setChartData({
      labels,
      datasets: chartDatasets as ChartData['datasets'],
    });

    setChartOptions(createChartOptions(-suggestedMinMax, suggestedMinMax));
  }, [d, use_external_expense]);

  return <Chart ref={chartRef} type={'bar'} data={chartData} options={chartOptions} style={{ height: 600 }} />;
};

const MonthlyAnalysisChart = ({
  d,
  use_external_expense,
}: {
  d: PlDashboardMonthlyAnalysis;
  use_external_expense: boolean;
}) => {
  const [chartData, setChartData] = useState<ChartData>({ datasets: [] });
  // const [chartOptions, setChartOptions] = useState<ChartOptions>(JSON.parse(JSON.stringify(defaultChartOptions)) as ChartOptions);
  const [chartOptions, setChartOptions] = useState<ChartOptions>({});
  const chartRef = useRef<ChartJS>(null);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const labels = d.target_year_month.map((ym) => {
      const month = moment(`${ym}01`).month();
      // return moment.monthsShort(month);
      return `${month + 1}月`;
    });

    // 月ごとの収益の合算
    const sumProfits = addArrays(
      d.sales_completed_amounts,
      d.sales_contracting_amounts,
      d.sales_under_negotiation_amounts,
      d.sales_detail_proposal_amounts,
      d.sales_initial_proposal_amounts,
      d.sales_attack_list_amounts
    );

    // 月ごとの損益の合算
    const sumLosses = use_external_expense
      ? addArrays(
          d.external_expense_amounts,
          d.purchase_completed_amounts,
          d.purchase_contracting_amounts,
          d.purchase_preparing_amounts
        )
      : addArrays(d.purchase_completed_amounts, d.purchase_contracting_amounts, d.purchase_preparing_amounts);

    const maxProfit = Math.max(...sumProfits);
    const maxLoss = Math.min(...sumLosses);
    const suggestedMinMax = Math.abs(maxProfit) > Math.abs(maxLoss) ? Math.abs(maxProfit) : Math.abs(maxLoss);

    const chartDatasets = [];
    chartDatasets.push({
      type: 'line',
      label: '収入−支出',
      tension: 0.1,
      pointRadius: 0,
      pointBackgroundColor: '#82D616',
      borderColor: '#82D616',
      borderWidth: 3,
      backgroundColor: createGradientStroke(chart.ctx),
      data: d.balance_amounts,
      fill: true,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.completed, // 契約終了
      backgroundColor: 'rgba(69, 104, 205, 1)',
      data: d.sales_completed_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.contracting, // 契約中
      backgroundColor: 'rgba(69, 104, 205, 0.8)',
      data: d.sales_contracting_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.under_negotiation, // 交渉中
      backgroundColor: 'rgba(116, 182, 248, 1)',
      data: d.sales_under_negotiation_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.detail_proposal, // 詳細提案
      backgroundColor: 'rgba(116, 182, 248, 0.8)',
      data: d.sales_detail_proposal_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.initial_proposal, // 初期提案
      backgroundColor: 'rgba(116, 182, 248, 0.6)',
      data: d.sales_initial_proposal_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.sales_order_phase_labels.attack_list, // アタックリスト
      backgroundColor: 'rgba(116, 182, 248, 0.4)',
      data: d.sales_attack_list_amounts,
      maxBarThickness: 30,
    });
    if (use_external_expense) {
      chartDatasets.push({
        type: 'bar',
        label: 'Node管理外費用',
        backgroundColor: '#495057',
        data: d.external_expense_amounts,
        maxBarThickness: 30,
      });
    }
    chartDatasets.push({
      type: 'bar',
      label: d.purchase_order_phase_labels.completed, // 契約終了
      backgroundColor: '#9542C1',
      data: d.purchase_completed_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.purchase_order_phase_labels.contracting, // 契約中
      backgroundColor: '#A368CE',
      data: d.purchase_contracting_amounts,
      maxBarThickness: 30,
    });
    chartDatasets.push({
      type: 'bar',
      label: d.purchase_order_phase_labels.preparing, // 稟議準備
      backgroundColor: '#E1AEF8',
      data: d.purchase_preparing_amounts,
      maxBarThickness: 30,
    });
    setChartData({
      labels,
      datasets: chartDatasets as ChartData['datasets'],
    });

    setChartOptions(createChartOptions(-suggestedMinMax, suggestedMinMax));
  }, [d, use_external_expense]);

  return <Chart ref={chartRef} type={'bar'} data={chartData} options={chartOptions} style={{ height: 600 }} />;
};
